/* eslint-disable @typescript-eslint/member-ordering */
import { inject, Injectable } from '@angular/core';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { Params } from '@angular/router';

import { TranslateService } from '@jht/brand-translate';

import { Gallery, SiteContent, ThreeSixty, Video } from '../models';
import { DataService } from './data.service';
import { FileService } from './file.service';

@Injectable({
    providedIn: 'root',
})
export class ContentService {
    pageData!: SiteContent;
    isHome!: boolean;
    params!: Params;
    private dataService = inject(DataService);
    private fileService = inject(FileService);
    private domSanitizer  = inject(DomSanitizer);
    private translateService  = inject(TranslateService);
    
    constructor(
    ) {}

    setPageDataFromParams(params: Params): void {
        if (!params?.category) {
            return;
        }

        this.params = params;
        const type: string = params.category === 'home' ? params.category : 'category';

        this.isHome = type === 'home';
        const content: any = this.getSiteContentByType(type);

        this.pageData = this.parseContentByParam(content, params)[0];
    }

    getGalleryPageContent(): Gallery | undefined {
        return this.getChildrensChildrenByRoute(this.pageData, 'gallery')?.filter(
            (gallery) => gallery.route === this.params['gallery-title']
        )[0] as Gallery | undefined;
    }

    getThreeSixtyPageContent(): ThreeSixty | undefined {
        return this.getPageContentByParam('three-sixty', 'threeSixty-title');
    }

    getColorConfigPageContent(): SiteContent | undefined {
        return this.getPageContentByParam('color-configurator', 'configurator-title');
    }

    getVideoPageContent(): Video | undefined {
        return this.getPageContentByParam('video', 'video-title');
    }

    async getGridPageContent(): Promise<SiteContent> {
        const pageContent: SiteContent = { ...this.pageData };
        pageContent.headline = this.getTranslationIfExists(
            pageContent.headline
        );
        pageContent['background-image'] = await this.getAndSanitizeBGImage(
            pageContent['background-image'] as string
        );

        return pageContent;
    }

    getNavigation(): SiteContent[] {
        return this.dataService.getNavData();
    }

    private getTranslationIfExists(value: string) {
        const keyVal = this.translateService.instant(value);
        return keyVal === value ? value : keyVal;
    }

    private getChildrensChildrenByRoute(data: SiteContent | Gallery, route: string) {
        return data?.children.filter((items) => items.route === route)[0]
            ?.children;
    }

    private getChildren(data: SiteContent, param: string): SiteContent[] {
        return data.children.filter((items) => items.route === param);
    }

    private getPageContentByParam(
        route: string,
        param: string
    ): any {
        const pageContent = this.getChildrensChildrenByRoute(
            this.pageData,
            route
        );

        const result = pageContent
            ? pageContent.filter(
                  (item: any) => item.route === this.params[param]
              )
            : [];
        return result[0];
    }

    async getBGImage(bgImage: string): Promise<string> {
        const urlBg = this.getTranslationIfExists(bgImage);
        const file: string = urlBg ? urlBg : '';
        const imagePath: string = await this.fileService.getFilePath(
            file,
            'media',
            true
        );
        return imagePath;
    }

    async getAndSanitizeBGImage(bgImage: string): Promise<SafeStyle> {
        const imagePath = await this.getBGImage(bgImage);
        return this.domSanitizer.bypassSecurityTrustStyle(
            'url(' + imagePath + ')'
        );
    }

    getSiteContentByType(type: string) {
       return this.dataService.allData.filter((e: any) => e.type === type)
    }

    private parseContentByParam(
        data: SiteContent[],
        params: Params
    ): SiteContent[]{
        let result: SiteContent[];
        result = data.filter((items) => items.route === params.category);
        if (params['level-1']) {
            result = this.getChildren(result[0], params['level-1']);
            if (params['level-2']) {
                result = this.getChildren(result[0], params['level-2']);
                if (params['level-3']) {
                    result = this.getChildren(result[0], params['level-3']);
                }
            }
        }
        return result;
    }
}
